import { guardNever, uuid } from "@hex/common";
import { Draft, castDraft, produce } from "immer";

import {
  WorkbenchTab,
  WorkbenchTabGroup,
  WorkbenchTabGroupId,
  WorkbenchTabId,
} from "./tabs.types";
import { WorkbenchViewport } from "./viewport.types";

/**
 * Filters all tabs across all tab groups that match the provided predicate.
 */
export function filterTabs<T extends WorkbenchTab>(
  state: WorkbenchTabGroup<T>[],
  tabPredicate: (tab: T, tg: WorkbenchTabGroup<T>) => boolean,
): T[] {
  return state.flatMap((tg) => tg.tabs.filter((tab) => tabPredicate(tab, tg)));
}

/**
 * Creates a new tab within the specified tab group,
 * or a new tab group if none is specified.
 */
export function newTab<T extends WorkbenchTab>(
  state: WorkbenchTabGroup<T>[],
  tab: T,
  tabGroupId: WorkbenchTabGroupId | null,
): WorkbenchTabGroup<T>[] {
  return produce(state, (tgs) => {
    const existingTg = tgs.find((tg) => tg.id === tabGroupId);
    if (existingTg) {
      existingTg.tabs = existingTg.tabs.filter((t) => !t.isPreview);
      existingTg.tabs.push(castDraft(tab));
      existingTg.currentTabId = tab.id;
    } else {
      tgs.push({
        id: tabGroupId ?? WorkbenchTabGroupId.check(uuid()),
        tabs: [castDraft(tab)],
        currentTabId: tab.id,
      });
    }
  });
}

/**
 * Focus a tab, optionally promoting it from a preview tab.
 * If the tab does not exist, nothing happens.
 */
export function focusTab<T extends WorkbenchTab>(
  state: WorkbenchTabGroup<T>[],
  tabId: WorkbenchTabId,
  promotePreview: boolean = false,
): WorkbenchTabGroup<T>[] {
  return produce(state, (tgs) => {
    for (const tg of tgs) {
      for (const tab of tg.tabs) {
        if (tab.id === tabId) {
          if (promotePreview && tab.isPreview) {
            tab.isPreview = false; // no longer a preview
          }
          tg.currentTabId = tab.id;
          return;
        }
      }
    }
  });
}

/**
 * Close the specified tab.
 * If the tab does not exist, does nothing.
 */
export function closeTab<T extends WorkbenchTab>(
  state: WorkbenchTabGroup<T>[],
  tabId: WorkbenchTabId,
): WorkbenchTabGroup<T>[] {
  return produce(state, (tgs) => {
    for (const tg of tgs) {
      tg.tabs = tg.tabs.filter((t) => t.id !== tabId);
      tg.currentTabId =
        tg.currentTabId === tabId ? tg.tabs[0]?.id : tg.currentTabId;
    }
    // close any empty tab groups
    tgs.splice(0, tgs.length, ...tgs.filter((tg) => tg.tabs.length > 0));
  });
}

/**
 * Update the specified tab.
 * If the tab does not exist, does nothing.
 */
export function updateTab<T extends WorkbenchTab>(
  state: WorkbenchTabGroup<T>[],
  tabId: WorkbenchTabId,
  producerFn: (tab: Draft<T>) => void | Draft<T>,
): WorkbenchTabGroup<T>[] {
  return produce(state, (tgs) => {
    for (const tg of tgs) {
      for (const [idx, tab] of tg.tabs.entries()) {
        if (tab.id === tabId) {
          tg.tabs[idx] = produce(tab, producerFn);
          return;
        }
      }
    }
  });
}

/**
 * Synchronize the viewport with the tab groups, treating the tab groups
 * as the source of truth. Makes the minimum changes to the viewport possible.
 */
export function updateViewportToMatchTabGroups(
  tabGroups: WorkbenchTabGroup[],
  viewport: WorkbenchViewport,
): WorkbenchViewport {
  if (tabGroups.length === 0) {
    // no tab groups, replace with the empty viewport
    if (viewport.type === "empty") {
      return viewport;
    }
    return { type: "empty" };
  } else if (tabGroups.length === 1) {
    // one tab group, ensure the viewport is exactly equal to it
    if (
      viewport.type === "tabGroup" &&
      viewport.tabGroupId === tabGroups[0].id
    ) {
      return viewport;
    }
    return { type: "tabGroup", tabGroupId: tabGroups[0].id };
  } else {
    // multiple tab groups, ensure each one is represented in the viewport
    // splitting until that is true
    if (viewport.type === "split") {
      // check that every tab group is accounted for
      const viewportTabGroupIds = getTabGroupsInViewport(viewport);
      if (tabGroups.every((tg) => viewportTabGroupIds.has(tg.id))) {
        return viewport;
      }
      // ...fall through into recreating the viewport (this could be improved)
    }

    // If not a split, create a new horizontal split evenly between each tab group
    return {
      type: "split",
      orientation: "horizontal",
      split: tabGroups.map((tg) => ({
        viewport: { type: "tabGroup", tabGroupId: tg.id },
        ratio: 1 / tabGroups.length,
      })),
    };
  }
}

/**
 * Synchronize the tab groups with the viewport, treating the viewport
 * as the source of truth. Makes the minimum changes to the tab groups possible.
 */
export function updateTabGroupsToMatchViewport<T extends WorkbenchTab>(
  viewport: WorkbenchViewport,
  tabGroups: WorkbenchTabGroup<T>[],
): WorkbenchTabGroup<T>[] {
  const viewportTabGroupIds = getTabGroupsInViewport(viewport);
  if (
    tabGroups.length === viewportTabGroupIds.size &&
    tabGroups.every((tg) => viewportTabGroupIds.has(tg.id))
  ) {
    return tabGroups;
  } else {
    return tabGroups.filter((tg) => viewportTabGroupIds.has(tg.id));
  }
}

function getTabGroupsInViewport(
  viewport: WorkbenchViewport,
): Set<WorkbenchTabGroupId> {
  const viewportTabGroupIds = new Set<WorkbenchTabGroupId>();
  const searchStack = [viewport];
  while (searchStack.length) {
    const searchViewport = searchStack.pop()!;
    switch (searchViewport.type) {
      case "tabGroup":
        viewportTabGroupIds.add(searchViewport.tabGroupId);
        break;
      case "split":
        searchStack.push(...searchViewport.split.map((s) => s.viewport));
        break;
      case "empty":
        break; // nothing to search for here
      default:
        guardNever(searchViewport, searchViewport);
    }
  }
  return viewportTabGroupIds;
}
